<div class="space-y-0">
  @for (section of sortedSections(); track section.sectionId; let idx = $index) {
    <div class="border-b border-gray-100 last:border-b-0">
      <!-- Section Header -->
      <button
        (click)="toggleSection('section-' + idx)"
        class="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors cursor-pointer"
      >
        <div class="flex items-center gap-2">
          <span class="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {{ section.title }}
          </span>
          <span class="text-[10px] text-gray-400 font-normal">
            Section {{ idx + 1 }}
          </span>
        </div>
        <lucide-icon
          [img]="icons.ChevronDown"
          class="w-4 h-4 text-gray-400 transition-transform duration-200"
          [class.rotate-180]="isSectionExpanded('section-' + idx)"
        ></lucide-icon>
      </button>

      <!-- Section Content -->
      @if (isSectionExpanded('section-' + idx)) {
        <div class="px-4 pb-3 space-y-3">
          @for (item of section.items; track item.itemId) {
            <div class="bg-white rounded-lg border border-gray-100 p-3 space-y-2">
              <!-- Item Header -->
              <div class="flex items-center justify-between gap-3">
                <span class="text-sm font-semibold text-gray-900">
                  {{ item.questionIdentifier }}
                </span>
              </div>

              <!-- Question Text (full width) -->
              @if (item.questionText) {
                @if (!isItemExpanded(item.itemId)) {
                  <div class="flex items-center gap-1 text-xs text-gray-500">
                    <span class="truncate">{{ item.questionText }}</span>
                    @if (item.questionText.length > 40) {
                      <button
                        (click)="toggleItem(item.itemId)"
                        class="text-[10px] text-violet-600 hover:text-violet-700 font-medium whitespace-nowrap flex-shrink-0"
                      >Show more</button>
                    }
                  </div>
                } @else {
                  <p class="text-xs text-gray-500">
                    {{ item.questionText }}
                    <button
                      (click)="toggleItem(item.itemId)"
                      class="text-[10px] text-violet-600 hover:text-violet-700 font-medium ml-1"
                    >Show less</button>
                  </p>
                }
              }

              <!-- Progress Bar -->
              <div class="h-1 bg-gray-100 rounded-full overflow-hidden flex">
                @for (grade of getGradeWidths(item); track grade.grade) {
                  @if (grade.width > 0) {
                    <div
                      class="h-full transition-all duration-300"
                      [class]="grade.colorClass"
                      [style.width.%]="grade.width"
                    ></div>
                  }
                }
              </div>

              <!-- Legend -->
              <div class="flex items-center gap-3 text-[10px] flex-wrap">
                @for (grade of getGradeLegend(item); track grade.grade) {
                  <div class="flex items-center gap-1">
                    <span class="w-2 h-2 rounded-full" [class]="grade.dotClass"></span>
                    <span class="text-gray-500">{{ grade.grade }} ({{ grade.count }})</span>
                  </div>
                }
              </div>

              <!-- Common Issues (only shown if there are issues with 2+ affected students) -->
              @if (item.commonIssues && item.commonIssues.length > 0) {
                <div class="mt-2 space-y-1">
                  @for (issue of item.commonIssues; track issue.issue) {
                    <div class="px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-1.5">
                      <lucide-icon
                        [img]="icons.AlertCircle"
                        class="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-px"
                      ></lucide-icon>
                      <div class="leading-tight flex-1">
                        <span class="text-[11px] text-amber-600">{{ issue.issue }}</span>
                        <span class="text-[10px] text-amber-500 ml-1">({{ issue.affectedCount }} students)</span>
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Student Feedback Section (students with < A grade) -->
              @if (getStudentFeedback(item.itemId).length > 0) {
                <div class="mt-2">
                  <button
                    type="button"
                    (click)="toggleStudentFeedback(item.itemId)"
                    class="flex items-center gap-1.5 text-[11px] text-violet-600 hover:text-violet-700 font-medium cursor-pointer"
                  >
                    <lucide-icon [img]="icons.Users" class="w-3 h-3"></lucide-icon>
                    <span>Students needing attention ({{ getStudentFeedback(item.itemId).length }})</span>
                    <lucide-icon
                      [img]="icons.ChevronDown"
                      class="w-3 h-3 transition-transform duration-200"
                      [class.rotate-180]="isStudentFeedbackExpanded(item.itemId)"
                    ></lucide-icon>
                  </button>

                  @if (isStudentFeedbackExpanded(item.itemId)) {
                    <div class="mt-2 space-y-2">
                      @for (student of getStudentFeedback(item.itemId); track student.studentId) {
                        <div class="px-2.5 py-2 bg-gray-50 border border-gray-100 rounded-lg">
                          <div class="flex items-center gap-2 mb-1.5">
                            <span class="text-xs font-medium text-gray-800">{{ student.studentName }}</span>
                            <span
                              class="text-[10px] font-semibold"
                              [ngClass]="getScoreColorClass(student.score.gradeLetter)"
                            >
                              {{ student.score.earnedPoints }}/{{ student.score.maxPoints }}
                              ({{ student.score.gradeLetter }})
                            </span>
                          </div>
                          <p class="text-[11px] text-gray-600 leading-relaxed">{{ student.feedback }}</p>
                        </div>
                      }
                    </div>
                  }
                </div>
              } @else if (isLoadingFeedback()) {
                <div class="mt-2 flex items-center gap-1.5 text-[11px] text-gray-400">
                  <span class="animate-pulse">Loading student feedback...</span>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }
</div>
